import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type ImportProposalType = 'project' | 'strategy';

export interface ProposalFields {
  description: string | null;
  budget: number | null;
  start_date: string | null;
  end_date: string | null;
  portfolio: string | null;
  // Read from a whole project dossier (see run-document-import); null on per-file proposals.
  spent: number | null;
  progress: number | null;
  health: string | null;
  code: string | null;
  manager: string | null;
  sponsor: string | null;
  status: string | null;
  currency: string | null;
}

export type ProposalFieldKey = 'name' | 'description' | 'budget' | 'start_date' | 'end_date' | 'portfolio' | 'spent' | 'progress' | 'health';
export type ProposalOverrides = Partial<Record<ProposalFieldKey, string | number | null>>;

export interface ConflictAlt {
  value: string | number;
  candidate_ids: string[];
}

export interface MatchedProject {
  id: string;
  name: string;
  description: string | null;
  budget: number | null;
  start_date: string | null;
  end_date: string | null;
  portfolio_id: string | null;
  spent: number | null;
  progress: number | null;
  health: string | null;
}

export interface ImportProposal {
  id: string;
  proposal_type: ImportProposalType;
  name: string;
  fields: ProposalFields;
  conflicts: Record<string, ConflictAlt[]>;
  matched_project_id: string | null;
  matched_project: MatchedProject | null;
  confidence: number;
  needs_attention: boolean;
  sources: string[];
  /** Number of documents the proposal was read from (a dossier candidate covers many). */
  sourceCount: number;
}

export interface UndoResult {
  reverted: number;
  skipped: Array<{ target_id: string; name: string; reason: string }>;
}

// "Undo this import" is offered for the reviewer's own latest batch for a
// day; older imports are history, not something to revert from a banner.
const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

const EMPTY_FIELDS: ProposalFields = {
  description: null, budget: null, start_date: null, end_date: null, portfolio: null,
  spent: null, progress: null, health: null, code: null, manager: null, sponsor: null, status: null, currency: null,
};

export const useImportProposals = (type: ImportProposalType) => {
  const { user } = useAuth();
  const [proposals, setProposals] = useState<ImportProposal[]>([]);
  const [leftoverCount, setLeftoverCount] = useState(0);
  const [undoableBatchId, setUndoableBatchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const { data: rows, error } = await supabase
      .from('import_proposals')
      .select('id, proposal_type, name, fields, conflicts, matched_project_id, confidence, needs_attention')
      .eq('status', 'pending')
      .eq('proposal_type', type)
      .order('needs_attention', { ascending: true })
      .order('name', { ascending: true })
      .limit(500);

    if (error) {
      console.error('Failed to load import proposals:', error);
      setLoading(false);
      return;
    }

    const ids = (rows ?? []).map((r) => r.id);
    const matchedIds = [...new Set((rows ?? []).map((r) => r.matched_project_id).filter((x): x is string => !!x))];

    const [memberRes, projectRes, leftoverRes, batchRes] = await Promise.all([
      ids.length
        ? supabase.from('document_candidates').select('proposal_id, source_document_id, source_document_ids, synced_documents(file_name)').in('proposal_id', ids)
        : Promise.resolve({ data: [], error: null }),
      matchedIds.length
        ? supabase.from('projects_sync').select('id, name, description, budget, start_date, end_date, portfolio_id, spent, progress, health').in('id', matchedIds)
        : Promise.resolve({ data: [], error: null }),
      type === 'project'
        ? supabase
            .from('document_candidates')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'suggested')
            .is('proposal_id', null)
            .in('candidate_type', ['budget', 'portfolio'])
        : Promise.resolve({ count: 0, error: null }),
      supabase
        .from('import_batches')
        .select('id, applied_at, import_batch_items!inner(target_table)')
        .eq('applied_by', user.id)
        .is('undone_at', null)
        .eq('import_batch_items.target_table', type === 'project' ? 'projects_sync' : 'strategy_items')
        .gte('applied_at', new Date(Date.now() - UNDO_WINDOW_MS).toISOString())
        .order('applied_at', { ascending: false })
        .limit(1),
    ]);

    const sourcesByProposal = new Map<string, Set<string>>();
    const docIdsByProposal = new Map<string, Set<string>>();
    type MemberRow = {
      proposal_id: string | null;
      source_document_id: string | null;
      source_document_ids: string[] | null;
      synced_documents: { file_name: string | null } | null;
    };
    for (const m of (memberRes.data ?? []) as MemberRow[]) {
      if (!m.proposal_id) continue;
      if (!sourcesByProposal.has(m.proposal_id)) sourcesByProposal.set(m.proposal_id, new Set());
      if (!docIdsByProposal.has(m.proposal_id)) docIdsByProposal.set(m.proposal_id, new Set());
      const docIds = docIdsByProposal.get(m.proposal_id)!;
      if (m.source_document_id) docIds.add(m.source_document_id);
      for (const id of m.source_document_ids ?? []) docIds.add(id);
      const file = m.synced_documents?.file_name;
      if (file) sourcesByProposal.get(m.proposal_id)!.add(file);
    }
    const projectById = new Map(((projectRes.data ?? []) as MatchedProject[]).map((p) => [p.id, p]));

    setProposals(
      (rows ?? []).map((r) => ({
        id: r.id,
        proposal_type: r.proposal_type as ImportProposalType,
        name: r.name,
        fields: { ...EMPTY_FIELDS, ...((r.fields ?? {}) as Partial<ProposalFields>) },
        conflicts: (r.conflicts ?? {}) as unknown as Record<string, ConflictAlt[]>,
        matched_project_id: r.matched_project_id,
        matched_project: r.matched_project_id ? projectById.get(r.matched_project_id) ?? null : null,
        confidence: Number(r.confidence),
        needs_attention: r.needs_attention,
        sources: [...(sourcesByProposal.get(r.id) ?? [])].sort(),
        sourceCount: docIdsByProposal.get(r.id)?.size ?? 0,
      })),
    );
    setLeftoverCount(('count' in leftoverRes ? leftoverRes.count : 0) ?? 0);
    setUndoableBatchId(((batchRes.data ?? []) as Array<{ id: string }>)[0]?.id ?? null);
    setLoading(false);
  }, [user, type]);

  useEffect(() => {
    load();
    const channel = supabase
      .channel(`import-proposals-${type}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'import_proposals' }, () => { load(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load, type]);

  const apply = useCallback(
    async (items: Array<{ proposal_id: string; overrides?: ProposalOverrides }>) => {
      setBusy(true);
      const { data, error } = await supabase.rpc('apply_import_proposals', {
        p_items: items.map((i) => ({ proposal_id: i.proposal_id, overrides: i.overrides ?? {} })),
      });
      setBusy(false);
      if (error) return { ok: false as const, error: error.message };
      // Refresh signals from the newly imported projects. Best effort: roles
      // outside admin/PMO/executive are refused by the function, which is fine.
      void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
      await load();
      return { ok: true as const, batchId: data as string };
    },
    [load],
  );

  const dismiss = useCallback(
    async (ids: string[], reason: string) => {
      setBusy(true);
      const { error } = await supabase.rpc('dismiss_import_proposals', { p_ids: ids, p_reason: reason });
      setBusy(false);
      if (error) return { ok: false as const, error: error.message };
      await load();
      return { ok: true as const };
    },
    [load],
  );

  const undo = useCallback(
    async (batchId: string) => {
      setBusy(true);
      const { data, error } = await supabase.rpc('undo_import_batch', { p_batch_id: batchId });
      setBusy(false);
      if (error) return { ok: false as const, error: error.message };
      await load();
      return { ok: true as const, result: data as unknown as UndoResult };
    },
    [load],
  );

  return { proposals, leftoverCount, loading, busy, undoableBatchId, apply, dismiss, undo, reload: load };
};
